import { PUBLIC_ORGANIZATION_ID } from '../ent/root-entity-ids';
import { ROUTE } from './route-utils';
// User type for display purposes
type ZeroUser = {
  firstName?: string | null;
  lastName?: string | null;
  primaryEmailAddress?: string | null;
};

export interface AddCreateMenuItem {
  readonly id: string;
  readonly disabled?: boolean;
  readonly label?: string;
  readonly i18n?: string;
  readonly onClick?: () => void;
  readonly children?: AddCreateMenuItem[];
}

export enum AddCreateMenuItemId {
  FoodAndBeverage = 'foodAndBeverage',
  Communication = 'communication',
  Team = 'team',
  Add86Item = 'add86Item',
  CreateBeverageMenu = 'createBeverageMenu',
  CreateFoodMenu = 'createFoodMenu',
  CreateRecipe = 'createRecipe',
  AddToManagerLog = 'addToManagerLog',
  InviteEmployee = 'inviteEmployee'
}

export function getUserDisplayName(user: ZeroUser): string {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name || user?.primaryEmailAddress || 'Unknown';
}

/** Up to two letters for avatar fallbacks. */
export function getUserInitials(user: ZeroUser): string {
  const initials = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .map(part => part!.charAt(0))
    .join('');
  return (initials || getUserDisplayName(user).charAt(0)).toUpperCase();
}

export type AddCreateMenuCallbacks = {
  onAdd86Item?: () => void;
  onCreateBeverageMenu?: () => void;
  onCreateFoodMenu?: () => void;
  onCreateRecipe?: () => void;
  onAddToManagerLog?: () => void;
  onInviteEmployee?: () => void;
};

export function getAddCreateMenu(
  t: any,
  callbacks: AddCreateMenuCallbacks = {}
): AddCreateMenuItem[] {
  return [
    {
      id: AddCreateMenuItemId.FoodAndBeverage,
      label: 'Food and beverage',
      children: [
        {
          id: AddCreateMenuItemId.Add86Item,
          label: 'Add 86 item',
          onClick: () => callbacks.onAdd86Item?.()
        },
        {
          id: AddCreateMenuItemId.CreateBeverageMenu,
          label: 'Create beverage menu',
          onClick: () => callbacks.onCreateBeverageMenu?.()
        },
        {
          id: AddCreateMenuItemId.CreateFoodMenu,
          label: 'Create food menu',
          onClick: () => callbacks.onCreateFoodMenu?.()
        },
        {
          id: AddCreateMenuItemId.CreateRecipe,
          label: 'Create recipe',
          onClick: () => callbacks.onCreateRecipe?.()
        }
      ]
    },
    { id: 'separator' },
    {
      id: AddCreateMenuItemId.Communication,
      label: 'Communication',
      children: [
        {
          id: AddCreateMenuItemId.AddToManagerLog,
          label: 'Add to manager log',
          onClick: () => callbacks.onAddToManagerLog?.()
        }
      ]
    },
    { id: 'separator' },
    {
      id: AddCreateMenuItemId.Team,
      label: 'Team',
      children: [
        {
          id: AddCreateMenuItemId.InviteEmployee,
          label: 'Invite employee',
          onClick: () => callbacks.onInviteEmployee?.()
        }
      ]
    }
  ];
}

export type ContextualAddScope =
  'all' | 'none' | readonly AddCreateMenuItemId[];

function organizationRoute(pathname: string, organizationId: string): string {
  const prefix = `/${organizationId}/`;
  const route = pathname.startsWith(prefix)
    ? pathname.slice(prefix.length)
    : pathname.replace(/^\//, '');
  return route.replace(/\/+$/, '');
}

/** Home hides Plus; messaging owns its own conversation action. */
export function getContextualAddCreateItemIds(
  pathname: string,
  organizationId: string
): ContextualAddScope {
  const route = organizationRoute(pathname, organizationId);

  switch (route) {
    case '':
    case ROUTE.HOME:
      return 'none';
    case ROUTE.EIGHTY_SIX:
      return [AddCreateMenuItemId.Add86Item];
    case ROUTE.TEAM:
    case ROUTE.EMPLOYEES:
      return [AddCreateMenuItemId.InviteEmployee];
    case ROUTE.LIBRARY:
      return [
        AddCreateMenuItemId.CreateRecipe,
        AddCreateMenuItemId.CreateBeverageMenu,
        AddCreateMenuItemId.CreateFoodMenu
      ];
    case `${ROUTE.LIBRARY}/${ROUTE.LIBRARY_MENUS}`:
      return [
        AddCreateMenuItemId.CreateBeverageMenu,
        AddCreateMenuItemId.CreateFoodMenu
      ];
    case `${ROUTE.BEVERAGES}/${ROUTE.RECIPES}`:
      return [AddCreateMenuItemId.CreateRecipe];
    case `${ROUTE.BEVERAGES}/${ROUTE.COCKTAIL_MENUS}`:
      return [AddCreateMenuItemId.CreateBeverageMenu];
    case `${ROUTE.FOOD}/${ROUTE.FOOD_MENUS}`:
      return [AddCreateMenuItemId.CreateFoodMenu];
    case ROUTE.MESSAGES:
      return 'none';
    case ROUTE.LOGBOOK:
      return [AddCreateMenuItemId.AddToManagerLog];
    default:
      return 'none';
  }
}

export function filterAddCreateMenu(
  items: readonly AddCreateMenuItem[],
  scope: ContextualAddScope
): AddCreateMenuItem[] {
  if (scope === 'none') return [];

  const allowed = scope === 'all' ? null : new Set<string>(scope);
  const groups = items
    .map(item => {
      if (item.id === 'separator' || !item.children) return item;
      const children = allowed
        ? item.children.filter(child => allowed.has(child.id))
        : item.children;
      return { ...item, children };
    })
    .filter(
      item => item.id === 'separator' || (item.children?.length ?? 0) > 0
    );

  return groups.filter((item, index, arr) => {
    if (item.id !== 'separator') return true;
    const prev = arr[index - 1];
    const next = arr[index + 1];
    return Boolean(
      prev && next && prev.id !== 'separator' && next.id !== 'separator'
    );
  });
}

export function addCreateMenuLeaves(
  items: readonly AddCreateMenuItem[]
): AddCreateMenuItem[] {
  return items.flatMap(item => item.children ?? []);
}

export interface UserMetadata {
  version: number;
  default_organization_id: string | undefined;
  organization_ids: string[]; // IDs of all organizations a user belongs to
  welcome_completed_at?: string;
}

export const EMPTY_USER_METADATA: UserMetadata = {
  version: 0,
  default_organization_id: undefined,
  organization_ids: [PUBLIC_ORGANIZATION_ID]
};

/** Orgs that are not the shared public catalog. */
export function workspaceOrganizationIds(
  organizationIds: readonly string[]
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const id of organizationIds) {
    if (id === PUBLIC_ORGANIZATION_ID || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * Workspace to route into: honor `defaultOrganizationId` when it is a real
 * membership, otherwise the first workspace org. Public-catalog membership
 * alone is not a workspace.
 */
export function resolveWorkspaceOrganizationId(
  organizationIds: readonly string[],
  defaultOrganizationId: string | undefined
): string | undefined {
  const workspaces = workspaceOrganizationIds(organizationIds);
  if (defaultOrganizationId && workspaces.includes(defaultOrganizationId)) {
    return defaultOrganizationId;
  }
  return workspaces[0];
}

export function hasWorkspaceMembership(metadata: UserMetadata): boolean {
  return Boolean(
    resolveWorkspaceOrganizationId(
      metadata.organization_ids,
      metadata.default_organization_id
    )
  );
}
